import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authAPI, socialAPI } from '../services/api';
import './Social.css';

function Social() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [accounts, setAccounts] = useState([]);
  const [facebookProfile, setFacebookProfile] = useState(null);
  const [youtubeChannel, setYoutubeChannel] = useState(null);
  const [instagramProfile, setInstagramProfile] = useState(null);
  const [instagramMedia, setInstagramMedia] = useState([]);
  const [youtubeVideos, setYoutubeVideos] = useState([]);
  const [facebookPages, setFacebookPages] = useState([]);
  const [instagramPublishMode, setInstagramPublishMode] = useState('single');
  const [instagramCaption, setInstagramCaption] = useState('');
  const [instagramImageUrl, setInstagramImageUrl] = useState('');
  const [instagramCarouselUrls, setInstagramCarouselUrls] = useState('');
  const [instagramPublishing, setInstagramPublishing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [instagramNeedsReconnect, setInstagramNeedsReconnect] = useState(false);
  const [instagramReconnectReason, setInstagramReconnectReason] = useState('');
  const [instagramLastVerifiedAt, setInstagramLastVerifiedAt] = useState(null);
  const [postMessage, setPostMessage] = useState('');
  const [postLink, setPostLink] = useState('');
  const [posting, setPosting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [facebookProfileImageBlobUrl, setFacebookProfileImageBlobUrl] = useState(null);
  const [facebookImageProxyFailed, setFacebookImageProxyFailed] = useState(false);

  const formatVerificationTime = (timestamp) => {
    if (!timestamp) {
      return '';
    }

    const verifiedDate = new Date(timestamp);
    const diffMs = Date.now() - verifiedDate.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);

    if (diffMinutes < 1) {
      return 'Last verified just now';
    }

    if (diffMinutes < 60) {
      return `Last verified ${diffMinutes}m ago`;
    }

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) {
      return `Last verified ${diffHours}h ago`;
    }

    return `Last verified ${verifiedDate.toLocaleString()}`;
  };

  const mapInstagramErrorMessage = (rawMessage) => {
    const decoded = decodeURIComponent(rawMessage || '').trim();
    const normalized = decoded.toLowerCase();

    if (!decoded) {
      return 'Instagram connection failed. Please try again.';
    }

    if (normalized.includes('another user')) {
      return 'This Instagram account is already connected to another user in this app.';
    }

    if (normalized.includes('access_denied') || normalized.includes('denied')) {
      return 'Instagram permission was denied. Please approve access to continue.';
    }

    if (normalized.includes('professional') || normalized.includes('business') || normalized.includes('creator')) {
      return 'Only Instagram Professional accounts (Business or Creator) can be connected.';
    }

    if (normalized.includes('expired') || normalized.includes('invalid token')) {
      return 'Instagram session expired. Reconnect your account to continue.';
    }

    return `Instagram: ${decoded}`;
  };

  useEffect(() => {
    // Check if returning from Facebook connect
    const facebookStatus = searchParams.get('facebook');
    if (facebookStatus === 'connected') {
      setMessage({ type: 'success', text: 'Facebook account connected successfully! 🎉' });
      // Clean up URL
      setSearchParams({});
    }
    // Check if returning from YouTube connect
    const youtubeStatus = searchParams.get('youtube');
    if (youtubeStatus === 'connected') {
      setMessage({ type: 'success', text: 'YouTube account connected successfully! 🎉' });
      setSearchParams({});
    }
    // Check if returning from Instagram connect
    const instagramStatus = searchParams.get('instagram');
    if (instagramStatus === 'connected') {
      setMessage({ type: 'success', text: 'Instagram account connected successfully! 🎉' });
      setSearchParams({});
    } else if (instagramStatus === 'error') {
      const errorMsg = searchParams.get('message') || 'Failed to connect Instagram';
      setMessage({ type: 'error', text: mapInstagramErrorMessage(errorMsg) });
      setSearchParams({});
    }
    loadSocialAccounts();
  }, []);

  useEffect(() => {
    return () => {
      if (facebookProfileImageBlobUrl) {
        URL.revokeObjectURL(facebookProfileImageBlobUrl);
      }
    };
  }, [facebookProfileImageBlobUrl]);

  const verifyInstagramConnection = async () => {
    try {
      const response = await authAPI.verifyInstagram();
      if (response.data?.valid) {
        setInstagramNeedsReconnect(false);
        setInstagramReconnectReason('');
        setInstagramLastVerifiedAt(new Date().toISOString());
        return true;
      }

      setInstagramNeedsReconnect(true);
      setInstagramReconnectReason(response.data?.error || 'Instagram session expired or invalid.');
      return false;
    } catch (error) {
      setInstagramNeedsReconnect(true);
      setInstagramReconnectReason(error.response?.data?.error || 'Instagram connection could not be verified.');
      return false;
    }
  };

  const loadSocialAccounts = async () => {
    try {
      const response = await authAPI.getSocialAccounts();
      setAccounts(response.data.accounts);
      
      // If Facebook is connected, get profile
      const fbAccount = response.data.accounts.find(a => a.platform === 'facebook' && a.is_connected);
      if (fbAccount) {
        loadFacebookProfile();
        loadFacebookPages();
        loadFacebookProfilePicture();
      } else {
        setFacebookImageProxyFailed(false);
        setFacebookProfileImageBlobUrl(null);
      }
      
      // If YouTube is connected, get channel info
      const ytAccount = response.data.accounts.find(a => a.platform === 'youtube' && a.is_connected);
      if (ytAccount) {
        loadYoutubeChannel();
        loadYoutubeVideos();
      }

      // If Instagram is connected, get profile and media
      const igAccount = response.data.accounts.find(a => a.platform === 'instagram' && a.is_connected);
      if (igAccount) {
        const isValid = await verifyInstagramConnection();
        if (isValid) {
          loadInstagramProfile();
          loadInstagramMedia();
        }
      } else {
        setInstagramNeedsReconnect(false);
        setInstagramReconnectReason('');
        setInstagramLastVerifiedAt(null);
      }
    } catch (error) {
      console.error('Load accounts error:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const loadFacebookProfile = async () => {
    try {
      const response = await socialAPI.getFacebookProfile();
      setFacebookProfile(response.data);
    } catch (error) {
      console.error('Load profile error:', error);
    }
  };

  const loadFacebookProfilePicture = async () => {
    try {
      const response = await socialAPI.getFacebookProfilePicture();
      const blob = response.data;

      if (!(blob instanceof Blob) || blob.size === 0) {
        setFacebookImageProxyFailed(true);
        setFacebookProfileImageBlobUrl(null);
        return;
      }

      const objectUrl = URL.createObjectURL(blob);
      setFacebookProfileImageBlobUrl((currentUrl) => {
        if (currentUrl) {
          URL.revokeObjectURL(currentUrl);
        }
        return objectUrl;
      });
      setFacebookImageProxyFailed(false);
    } catch (error) {
      console.error('Load Facebook profile picture error:', error);
      setFacebookImageProxyFailed(true);
      setFacebookProfileImageBlobUrl(null);
    }
  };

  const loadYoutubeChannel = async () => {
    try {
      const response = await socialAPI.getYouTubeStats();
      setYoutubeChannel(response.data);
    } catch (error) {
      console.error('Load YouTube channel error:', error);
    }
  };

  const loadInstagramProfile = async () => {
    try {
      const response = await socialAPI.getInstagramProfile();
      setInstagramProfile(response.data);
    } catch (error) {
      console.error('Load Instagram profile error:', error);
      if (error.response?.status === 400 || error.response?.status === 401) {
        setInstagramNeedsReconnect(true);
        setInstagramReconnectReason('Instagram session expired or invalid. Please reconnect your account.');
      }
    }
  };

  const loadInstagramMedia = async () => {
    try {
      const response = await socialAPI.getInstagramMedia(6);
      setInstagramMedia(response.data.media || []);
    } catch (error) {
      console.error('Load Instagram media error:', error);
      if (error.response?.status === 400 || error.response?.status === 401) {
        setInstagramNeedsReconnect(true);
        setInstagramReconnectReason('Instagram session expired or invalid. Please reconnect your account.');
      }
    }
  };

  const loadYoutubeVideos = async () => {
    try {
      const response = await socialAPI.getYouTubeVideos(5);
      setYoutubeVideos(response.data.videos || []);
    } catch (error) {
      console.error('Load YouTube videos error:', error);
    }
  };

  const loadFacebookPages = async () => {
    try {
      const response = await socialAPI.getFacebookPages();
      setFacebookPages(response.data.pages || []);
    } catch (error) {
      console.error('Load Facebook pages error:', error);
    }
  };

  const handleConnectFacebook = () => {
    authAPI.connectFacebook();
  };

  const handleConnectYouTube = () => {
    authAPI.connectYouTube();
  };

  const handleConnectInstagram = () => {
    authAPI.connectInstagram();
  };

  const handleDisconnectFacebook = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Facebook account?')) {
      return;
    }

    try {
      await authAPI.disconnectFacebook();
      setMessage({ type: 'success', text: 'Facebook account disconnected successfully' });
      setFacebookProfile(null);
      setFacebookImageProxyFailed(false);
      setFacebookProfileImageBlobUrl(null);
      loadSocialAccounts();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to disconnect' });
    }
  };

  const handleDisconnectYouTube = async () => {
    if (!window.confirm('Are you sure you want to disconnect your YouTube account?')) {
      return;
    }

    try {
      await authAPI.disconnectYouTube();
      setMessage({ type: 'success', text: 'YouTube account disconnected successfully' });
      setYoutubeChannel(null);
      loadSocialAccounts();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to disconnect' });
    }
  };

  const handleDisconnectInstagram = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Instagram account?')) {
      return;
    }

    try {
      await authAPI.disconnectInstagram();
      setMessage({ type: 'success', text: 'Instagram account disconnected successfully' });
      setInstagramProfile(null);
      setInstagramMedia([]);
      setInstagramNeedsReconnect(false);
      setInstagramReconnectReason('');
      setInstagramLastVerifiedAt(null);
      loadSocialAccounts();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to disconnect' });
    }
  };

  const handlePostToFacebook = async (e) => {
    e.preventDefault();
    
    if (!postMessage.trim()) {
      setMessage({ type: 'error', text: 'Please enter a message' });
      return;
    }

    setPosting(true);
    setMessage({ type: '', text: '' });

    try {
      await socialAPI.postToFacebook({
        message: postMessage,
        link: postLink || null
      });
      
      setMessage({ type: 'success', text: 'Posted to Facebook successfully! 🎉' });
      setPostMessage('');
      setPostLink('');
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to post to Facebook' });
    } finally {
      setPosting(false);
    }
  };

  const parseCarouselUrls = (rawValue) => {
    return rawValue
      .split('\n')
      .map(url => url.trim())
      .filter(Boolean);
  };

  const hasValidHttpUrl = (url) => /^https?:\/\//i.test(url);

  const handlePublishToInstagram = async (e) => {
    e.preventDefault();

    if (instagramNeedsReconnect) {
      setMessage({ type: 'error', text: 'Reconnect Instagram before publishing.' });
      return;
    }

    if (instagramPublishMode === 'single') {
      const imageUrl = instagramImageUrl.trim();
      if (!imageUrl) {
        setMessage({ type: 'error', text: 'Please add an image URL.' });
        return;
      }

      if (!hasValidHttpUrl(imageUrl)) {
        setMessage({ type: 'error', text: 'Image URL must start with http:// or https://.' });
        return;
      }
    }

    if (instagramPublishMode === 'carousel') {
      const urls = parseCarouselUrls(instagramCarouselUrls);

      if (urls.length < 2) {
        setMessage({ type: 'error', text: 'Carousel requires at least 2 image URLs.' });
        return;
      }

      if (urls.length > 10) {
        setMessage({ type: 'error', text: 'Carousel supports a maximum of 10 image URLs.' });
        return;
      }

      const invalidUrl = urls.find(url => !hasValidHttpUrl(url));
      if (invalidUrl) {
        setMessage({ type: 'error', text: `Invalid URL in carousel list: ${invalidUrl}` });
        return;
      }
    }

    setInstagramPublishing(true);
    setMessage({ type: '', text: '' });

    try {
      if (instagramPublishMode === 'single') {
        await socialAPI.publishToInstagram({
          image_url: instagramImageUrl.trim(),
          caption: instagramCaption.trim()
        });
      } else {
        await socialAPI.publishCarouselToInstagram({
          image_urls: parseCarouselUrls(instagramCarouselUrls),
          caption: instagramCaption.trim()
        });
      }

      setMessage({ type: 'success', text: 'Instagram post published successfully! 🎉' });
      setInstagramCaption('');
      setInstagramImageUrl('');
      setInstagramCarouselUrls('');
      loadInstagramMedia();
      loadInstagramProfile();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to publish to Instagram' });
    } finally {
      setInstagramPublishing(false);
    }
  };

  const isFacebookConnected = accounts.some(a => a.platform === 'facebook' && a.is_connected);
  const isYouTubeConnected = accounts.some(a => a.platform === 'youtube' && a.is_connected);
  const isInstagramConnected = accounts.some(a => a.platform === 'instagram' && a.is_connected);
  const facebookProfileImage =
    facebookProfile?.picture?.data?.url ||
    facebookProfile?.picture?.url ||
    (typeof facebookProfile?.picture === 'string' ? facebookProfile.picture : null) ||
    facebookPages?.[0]?.picture?.data?.url ||
    facebookPages?.[0]?.picture?.url ||
    (typeof facebookPages?.[0]?.picture === 'string' ? facebookPages[0].picture : null) ||
    null;
  const facebookResolvedImage = !facebookImageProxyFailed && facebookProfileImageBlobUrl
    ? facebookProfileImageBlobUrl
    : facebookProfileImage;
  const facebookDisplayName = facebookProfile?.name || facebookPages?.[0]?.name || 'Facebook account connected';
  const facebookDisplayEmail = facebookProfile?.email || null;

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  const connectedCount = accounts.filter(a => a.is_connected).length;

  return (
    <div className="social-container">
      <h1>Social Media Integration</h1>
      <p className="subtitle">Connect your social media accounts to share campaigns and earn points</p>

      {loadError && (
        <div className="message error">
          ⚠️ Failed to load social accounts. Please refresh the page or try again later.
        </div>
      )}

      {!loadError && connectedCount === 0 && (
        <div className="message" style={{ background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', padding: '16px', borderRadius: 8, marginBottom: 16 }}>
          🚀 <strong>Get started!</strong> Connect at least one social account below to start sharing campaigns and earning points.
        </div>
      )}

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="social-accounts">
        <div className="social-card facebook">
          <div className="social-header">
            <div className="social-icon">📘</div>
            <div>
              <h2>Facebook</h2>
              <p className="status">
                {isFacebookConnected ? '✓ Connected' : '○ Not Connected'}
              </p>
            </div>
          </div>

          {isFacebookConnected ? (
            <div className="social-content">
              {(facebookProfile || facebookPages.length > 0) && (
                <div className="profile-info">
                  {facebookResolvedImage ? (
                    <img 
                      src={facebookResolvedImage}
                      alt="Profile" 
                      className="profile-pic"
                      onError={() => {
                        setFacebookImageProxyFailed(true);
                        setFacebookProfileImageBlobUrl((currentUrl) => {
                          if (currentUrl) {
                            URL.revokeObjectURL(currentUrl);
                          }
                          return null;
                        });
                      }}
                    />
                  ) : (
                    <div className="profile-pic profile-pic-fallback">FB</div>
                  )}
                  <div>
                    <p><strong>{facebookDisplayName}</strong></p>
                    <p className="email">{facebookDisplayEmail || 'Email unavailable from Facebook profile'}</p>
                  </div>
                </div>
              )}

              <div className="post-form">
                <h3>Post to Facebook</h3>
                <p className="platform-note">
                  Facebook API posting is limited to Pages you manage. For personal timeline posts, use the <Link to="/track-post">Track Post</Link> tab workaround.
                </p>
                <form onSubmit={handlePostToFacebook}>
                  <div className="form-group">
                    <label>Message *</label>
                    <textarea
                      value={postMessage}
                      onChange={(e) => setPostMessage(e.target.value)}
                      placeholder="What's on your mind?"
                      rows="4"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Link (optional)</label>
                    <input
                      type="url"
                      value={postLink}
                      onChange={(e) => setPostLink(e.target.value)}
                      placeholder="https://example.com"
                    />
                  </div>

                  <button 
                    type="submit" 
                    disabled={posting}
                    className="btn-primary"
                  >
                    {posting ? 'Posting...' : 'Post to Facebook'}
                  </button>
                </form>
              </div>

              <button 
                onClick={handleDisconnectFacebook}
                className="btn-secondary"
              >
                Disconnect Facebook
              </button>

              {facebookPages.length > 0 && (
                <div className="facebook-pages">
                  <h3>Your Facebook Pages</h3>
                  <div className="pages-list">
                    {facebookPages.map(page => (
                      <div key={page.id} className="page-item">
                        <span className="page-name">📄 {page.name}</span>
                        {page.category && <span className="page-category">{page.category}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="social-content">
              <p>Connect your Facebook account to:</p>
              <ul>
                <li>✓ Share campaigns with one click</li>
                <li>✓ Earn points for social shares</li>
                <li>✓ Track engagement and reach</li>
                <li>✓ Login with Facebook</li>
              </ul>
              <div className="facebook-requirements">
                <p className="requirement-note">
                  ⓘ Facebook posting works only on Pages you manage. For personal timeline posts, use the <Link to="/track-post">Track Post</Link> tab workaround.
                </p>
              </div>
              <button 
                onClick={handleConnectFacebook}
                className="btn-primary"
              >
                Connect Facebook Account
              </button>
            </div>
          )}
        </div>

        {/* Placeholder for other platforms */}
        <div className="social-card youtube">
          <div className="social-header">
            <div className="social-icon">▶️</div>
            <div>
              <h2>YouTube</h2>
              <p className="status">
                {isYouTubeConnected ? '✓ Connected' : '○ Not Connected'}
              </p>
            </div>
          </div>

          {isYouTubeConnected ? (
            <div className="social-content">
              {youtubeChannel && (
                <div className="profile-info">
                  {youtubeChannel.thumbnail && (
                    <img 
                      src={youtubeChannel.thumbnail} 
                      alt="Channel" 
                      className="profile-pic"
                    />
                  )}
                  <div>
                    <p><strong>{youtubeChannel.title}</strong></p>
                    {youtubeChannel.customUrl && (
                      <p className="email">@{youtubeChannel.customUrl}</p>
                    )}
                  </div>
                </div>
              )}

              {youtubeChannel && (
                <div className="youtube-stats">
                  <div className="stat-item">
                    <span className="stat-value">{parseInt(youtubeChannel.subscriberCount || 0).toLocaleString()}</span>
                    <span className="stat-label">Subscribers</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{parseInt(youtubeChannel.viewCount || 0).toLocaleString()}</span>
                    <span className="stat-label">Views</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{parseInt(youtubeChannel.videoCount || 0).toLocaleString()}</span>
                    <span className="stat-label">Videos</span>
                  </div>
                </div>
              )}

              {youtubeVideos.length > 0 && (
                <div className="youtube-videos">
                  <h3>Recent Videos</h3>
                  <div className="videos-list">
                    {youtubeVideos.map(video => (
                      <a
                        key={video.id}
                        href={`https://www.youtube.com/watch?v=${video.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="video-item"
                      >
                        {video.thumbnail && (
                          <img src={video.thumbnail} alt={video.title} className="video-thumbnail" />
                        )}
                        <div className="video-info">
                          <p className="video-title">{video.title}</p>
                          <p className="video-date">{new Date(video.publishedAt).toLocaleDateString()}</p>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <button 
                onClick={handleDisconnectYouTube}
                className="btn-secondary"
              >
                Disconnect YouTube
              </button>
            </div>
          ) : (
            <div className="social-content">
              <p>Connect your YouTube account to:</p>
              <ul>
                <li>✓ Track your channel statistics</li>
                <li>✓ View your video performance</li>
                <li>✓ Earn points for video content</li>
                <li>✓ Login with Google/YouTube</li>
              </ul>
              <button 
                onClick={handleConnectYouTube}
                className="btn-primary"
              >
                Connect YouTube Account
              </button>
            </div>
          )}
        </div>

        <div className="social-card instagram">
          <div className="social-header">
            <div className="social-icon">📷</div>
            <div>
              <h2>Instagram</h2>
              <p className="status">
                {isInstagramConnected ? '✓ Connected' : '○ Not Connected'}
              </p>
              {isInstagramConnected && instagramLastVerifiedAt && (
                <p className="verification-timestamp">
                  {formatVerificationTime(instagramLastVerifiedAt)}
                </p>
              )}
            </div>
          </div>

          {isInstagramConnected ? (
            <div className="social-content">
              {instagramNeedsReconnect && (
                <div className="instagram-warning">
                  <p>
                    <strong>Reconnect required:</strong> {instagramReconnectReason || 'Instagram connection needs to be renewed.'}
                  </p>
                  <button
                    onClick={handleConnectInstagram}
                    className="btn-primary btn-instagram"
                  >
                    Reconnect Instagram Account
                  </button>
                </div>
              )}

              {instagramProfile && (
                <div className="profile-info">
                  {instagramProfile.profile_picture_url && (
                    <img 
                      src={instagramProfile.profile_picture_url} 
                      alt="Profile" 
                      className="profile-pic"
                    />
                  )}
                  <div>
                    <p><strong>@{instagramProfile.username}</strong></p>
                    {instagramProfile.name && (
                      <p className="email">{instagramProfile.name}</p>
                    )}
                  </div>
                </div>
              )}

              {instagramProfile && (
                <div className="instagram-stats">
                  <div className="stat-item">
                    <span className="stat-value">{parseInt(instagramProfile.followers_count || 0).toLocaleString()}</span>
                    <span className="stat-label">Followers</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{parseInt(instagramProfile.follows_count || 0).toLocaleString()}</span>
                    <span className="stat-label">Following</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{parseInt(instagramProfile.media_count || 0).toLocaleString()}</span>
                    <span className="stat-label">Posts</span>
                  </div>
                </div>
              )}

              <div className="post-form instagram-post-form">
                <h3>Publish to Instagram</h3>

                <div className="publish-mode-toggle">
                  <button
                    type="button"
                    className={`mode-btn ${instagramPublishMode === 'single' ? 'active' : ''}`}
                    onClick={() => setInstagramPublishMode('single')}
                    disabled={instagramPublishing}
                  >
                    Single Image
                  </button>
                  <button
                    type="button"
                    className={`mode-btn ${instagramPublishMode === 'carousel' ? 'active' : ''}`}
                    onClick={() => setInstagramPublishMode('carousel')}
                    disabled={instagramPublishing}
                  >
                    Carousel
                  </button>
                </div>

                <form onSubmit={handlePublishToInstagram}>
                  {instagramPublishMode === 'single' ? (
                    <div className="form-group">
                      <label>Image URL *</label>
                      <input
                        type="url"
                        value={instagramImageUrl}
                        onChange={(e) => setInstagramImageUrl(e.target.value)}
                        placeholder="https://example.com/your-image.jpg"
                        required
                        disabled={instagramPublishing}
                      />
                    </div>
                  ) : (
                    <div className="form-group">
                      <label>Carousel Image URLs * (one per line, 2-10)</label>
                      <textarea
                        value={instagramCarouselUrls}
                        onChange={(e) => setInstagramCarouselUrls(e.target.value)}
                        placeholder={'https://example.com/image-1.jpg\nhttps://example.com/image-2.jpg'}
                        rows="5"
                        required
                        disabled={instagramPublishing}
                      />
                    </div>
                  )}

                  <div className="form-group">
                    <label>Caption (optional)</label>
                    <textarea
                      value={instagramCaption}
                      onChange={(e) => setInstagramCaption(e.target.value)}
                      placeholder="Write your caption..."
                      rows="3"
                      maxLength={2200}
                      disabled={instagramPublishing}
                    />
                    <div className="caption-meta">{instagramCaption.length}/2200</div>
                  </div>

                  <p className="publish-note">
                    Note: Image URLs must be publicly accessible and Instagram-supported.
                  </p>

                  <button
                    type="submit"
                    className="btn-primary btn-instagram"
                    disabled={instagramPublishing || instagramNeedsReconnect}
                  >
                    {instagramPublishing ? 'Publishing...' : 'Publish to Instagram'}
                  </button>
                </form>
              </div>

              {instagramMedia.length > 0 && (
                <div className="instagram-media-grid">
                  <h3>Recent Posts</h3>
                  <div className="media-grid">
                    {instagramMedia.map(item => (
                      <a 
                        key={item.id} 
                        href={item.permalink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="media-item"
                      >
                        <img 
                          src={item.media_type === 'VIDEO' ? item.thumbnail_url : item.media_url} 
                          alt={item.caption ? item.caption.substring(0, 50) : 'Instagram post'}
                        />
                        <div className="media-overlay">
                          <span>❤️ {item.like_count || 0}</span>
                          <span>💬 {item.comments_count || 0}</span>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <button 
                onClick={handleDisconnectInstagram}
                className="btn-secondary"
              >
                Disconnect Instagram
              </button>
            </div>
          ) : (
            <div className="social-content">
              <p>Connect your Instagram account to:</p>
              <ul>
                <li>✓ Track post engagement and reach</li>
                <li>✓ View follower insights</li>
                <li>✓ Publish photos and carousels</li>
                <li>✓ Monitor your content performance</li>
              </ul>
              <div className="instagram-requirements">
                <p className="requirement-note">
                  ⓘ Requires an Instagram <strong>Business</strong> or <strong>Creator</strong> account.
                </p>
                <p className="requirement-note requirement-note-secondary">
                  Personal Instagram accounts are not supported by this integration.
                  <a href="https://help.instagram.com/502981923235522" target="_blank" rel="noopener noreferrer"> How to switch →</a>
                </p>
              </div>
              <button 
                onClick={handleConnectInstagram}
                className="btn-primary btn-instagram"
              >
                Connect Instagram Account
              </button>
            </div>
          )}
        </div>

        <div className="social-card twitter disabled">
          <div className="social-header">
            <div className="social-icon">🐦</div>
            <div>
              <h2>Twitter / X</h2>
              <p className="status">○ Coming Soon</p>
            </div>
          </div>
          <div className="social-content">
            <p>Twitter integration coming soon!</p>
          </div>
        </div>
      </div>

      <div className="tips">
        <h3>💡 Tips for Social Sharing</h3>
        <ul>
          <li>Post during peak hours (9-11 AM, 7-9 PM) for maximum engagement</li>
          <li>Include relevant hashtags to increase reach</li>
          <li>Use eye-catching images or videos when possible</li>
          <li>Engage with comments to boost post visibility</li>
          <li>Share authentic, genuine content that resonates with your audience</li>
        </ul>
      </div>
    </div>
  );
}

export default Social;
